import React from 'react';
import type { Office, Provider } from '~/types/Office';
import DefaultLayout from '~/layouts/DefaultLayout';
import Breadcrumbs from '~/components/Breadcrumbs';
import Container from '~/components/ui/Container';
import Panel from '~/components/ui/Panel';
import { Message } from '~/components/ui/Message';
import SubHeader from '~/components/office/SubHeader';
import ProfileLogo from '~/components/office/ProfileLogo';
import { searchQueryFromUrl } from '~/helpers/projectConstant';
import { slugMapped } from '~/helpers/scopeMap';
import { cn } from '~/helpers/utils';

export type OfficeProvidersProps = {
  office: Office;
  providers: Provider[];
  scope: string;
};

export const OfficeProviders: React.FC<OfficeProvidersProps> = ({
  office,
  providers,
  scope,
}) => {
  // Debug log removed for production
  const officeUrl = `/${scope}/${office.address.zipcode}/${office.slug}${searchQueryFromUrl ?? ''}`;
  const scopeSlug = scope !== undefined ? slugMapped[scope] ?? scope : scope;

  let breadcrumbLinks = [];
  if (scope && scopeSlug != 'advies') {
    breadcrumbLinks = [
      { name: 'Home', linkTo: '/', },
      { name: scopeSlug.charAt(0).toUpperCase() + scopeSlug.slice(1), linkTo: `/${scopeSlug}`, },
      { name: 'Zoekresultaat', linkTo: `/${scopeSlug}/zoekresultaat${searchQueryFromUrl || ''}`, },
      { name: office.name, linkTo: officeUrl, },
    ];
  } else {
    breadcrumbLinks = [
      { name: 'Home', linkTo: '/', },
      { name: office.name, linkTo: officeUrl, },
    ];
  }

  return (
    <DefaultLayout
      state="large"  
      breadcrumbs={<Breadcrumbs links={breadcrumbLinks} />}
    >
      <Container state="defaultLargeFirst" className="pb-16 md:pb-24">
        <SubHeader
          serverSideScope="Onze aanbieders"
          officeName={office.name}
          officeLogo={office.logoUrl}
          officeUrl={`${officeUrl}#aanbieders`}
        />
        <Panel>
          <h2 className="panel-title--primary">Het aanbod van {office.name}</h2>
          <div>
            <p>Er wordt gezocht naar de beste deal voor jou! Op deze pagina vind je het overzicht van alle aanbieders waarvan de producten worden vergeleken. De logo’s van de partners van Advieskeuze worden in kleur getoond.</p>
          </div>
        </Panel>
        <Panel>
          <h2 className="panel-title--primary">Alle aanbieders</h2>
          <div>
            {providers.length > 0  
              ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {providers.map((provider) => (
                    <div key={provider.id} className="flex flex-col items-center gap-3 mb-6">
                      <ProfileLogo 
                        src={provider.logoUrl} 
                        alt={provider.name}
                      />
                      <h3 className="text-sm font-display text-black font-bold word-wrap hyphens-auto">
                        {provider.name}
                      </h3>
                    </div>
                  ))}
                </div>
              ) 
              : <Message>Er zijn geen aanbieders gevonden voor dit kantoor</Message>
            }
          </div>
        </Panel>
      </Container>
    </DefaultLayout>
  );
};
